/**
 * Cart payload for the order builder — variant resolution, attributes, and
 * line-item properties.
 *
 * WHY THIS IS ITS OWN FILE. This is simultaneously the most dangerous logic in
 * the feature and the only part of it that is pure. Which variant, at what
 * quantity, with which attributes, is what decides whether the customer is
 * charged correctly and whether the deferred balance can be invoiced. Put it
 * beside `fetch` and `window.location` and it can only ever be tested through a
 * browser; keep it here and it is tested under plain node by
 * tests/order-builder.test.js.
 *
 * So: no fetch, no DOM, no window, no Date, no Math.random. The per-submit token
 * and the environment are passed IN, which is also what makes the output
 * deterministic enough to assert.
 *
 *   rf-pricing.js        pure arithmetic          <- required by this file
 *   rf-order-payload.js  what to send             <- this file
 *   rf-order-cart.js     the sending              <- Unit 9, transport only
 *
 * ALL MONEY IS INTEGER CENTS, as bare digit strings. docs/product-spec.md §9.
 *
 * Wrapped the same way as rf-pricing.js so one file serves as both a theme asset
 * (window.PhotokiveOrderPayload) and a CommonJS module. No build step.
 */
(function (root, factory) {
  'use strict';
  var pricing =
    typeof module === 'object' && module.exports
      ? require('./rf-pricing.js')
      : root.PhotokivePricing;
  var api = factory(pricing);
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.PhotokiveOrderPayload = api;
  }
})(typeof self !== 'undefined' ? self : this, function (pricing) {
  'use strict';

  if (!pricing) throw new Error('rf-order-payload.js requires rf-pricing.js first');

  /**
   * Attribute keys this builder no longer writes.
   *
   * They are still written, as empty strings, on every submit. /cart/clear.js does
   * NOT clear cart attributes and /cart/update.js MERGES them rather than
   * replacing, so a key that is simply dropped is a key inherited from whatever
   * the customer configured before — including a configuration they abandoned.
   * Setting a value to "" is what actually deletes it.
   *
   * There is no reset-my-namespace operation, so a rename is a two-release
   * migration: add the new key, list the old one here, and only remove it from
   * this list once no live cart can still be carrying it.
   */
  var RETIRED_ATTRIBUTE_KEYS = [
    // Written by the pre-rebuild builder in the old theme. Harmless if a returning
    // customer's cart still carries them, but they would be read as current.
    'pricing_version',
    // 'Albums ordered' WAS HERE, and left on 3 Sep 2026: the current contract
    // writes it as a cart attribute on every path (see the attribute set below).
    // The loop's `if (!(k in attributes))` guard means leaving it here would be
    // INERT rather than harmful -- and inert is exactly the problem. This list is
    // a two-release migration tool and a name in it is a claim that the builder
    // no longer writes the key. That claim would be false.
    'Balance due later',
    'Loose photos (billed with balance)',
    'Payment',
  ];

  var MODE_LABEL = {
    full: 'paid_in_full',
    deposit: 'deposit',
  };

  /** Line-item property text. Renders under the line at Shopify's checkout. */
  var MODE_TERMS = {
    full: 'Paid in full',
    deposit: '25% deposit — the balance is invoiced when your images are ready. ' +
             'A promo code applies to today’s payment and does not change the balance.',
  };

  /**
   * The Guarantee line's ONE customer-visible property (R4, R5).
   *
   * THE KEY IS PINNED HERE; THE SENTENCE IS THE MERCHANT'S. sections/cart.liquid
   * renders a line property as `Key: value` and skips only `_`-prefixed names, so
   * BOTH halves are read by a person -- on /cart, at Shopify's checkout, in the
   * confirmation email, and on Order.lineItems.customAttributes. The key is the
   * label fulfilment and support read on the order record, so it lives beside
   * MODE_TERMS rather than in a theme setting: a merchant renaming it would
   * rename the thing, not edit the copy. The sentence arrives through
   * opts.guaranteeNote, from the `guarantee_line_note` setting.
   *
   * THE $7.99 IS TYPED, AND THAT IS AN EXCEPTION TO A STATED RULE. sections/
   * order-builder.liquid's header says no price is ever a merchant setting,
   * because a typed price can advertise a rate the cart will not honour. This one
   * is not a rate this cart charges -- the line is $0.00 and no billing mechanism
   * exists yet (docs/guarantee-subscription-handoff.md) -- and binding it here
   * would need a SECOND copy of snippets/guarantee-card.liquid's annual-to-
   * monthly derivation in a file that cannot share it. So it is typed and PINNED
   * instead: tests/order-builder.test.js recomputes it from
   * tests/fixtures/album-list-prices.json's PK-GUARANTEE-ANNUAL row, and
   * docs/check-catalogue.sh holds that fixture against the live store. A
   * repricing fails the suite rather than leaving a stale figure at checkout.
   */
  var GUARANTEE_NOTE_KEY = 'Membership';
  var GUARANTEE_NOTE =
    'Free for your first 30 days once your images are ready, then $7.99/month ' +
    'billed annually. Nothing is charged for it today.';

  /** What resolve() calls the Guarantee in every refusal it can produce. */
  var GUARANTEE_LABEL = 'the Photokive Guarantee';

  function centsStr(n) {
    return String(Math.round(Number(n)));
  }

  /** Longest `_prior_submit_failures` value we will write. */
  var PRIOR_FAILURES_MAX = 120;

  /**
   * `{count, reasons: [...]}` -> `"2|422_cannot_find_variant,network"`, or `''`.
   *
   * Tolerates a malformed or absent record rather than throwing: the record comes
   * from sessionStorage, which the customer's browser may have mangled, cleared,
   * or refused to write in the first place. Losing the diagnostic is acceptable;
   * failing the submit over it is not.
   */
  function formatPriorFailures(rec) {
    if (!rec) return '';
    var count = Math.floor(Number(rec.count));
    if (!isFinite(count) || count < 1) return '';
    var reasons = Array.isArray(rec.reasons) ? rec.reasons : [];
    var cleaned = reasons
      .map(function (r) { return String(r).replace(/[^A-Za-z0-9_.-]/g, '_'); })
      .filter(Boolean)
      .slice(0, 5);
    var out = count + (cleaned.length ? '|' + cleaned.join(',') : '');
    return out.length > PRIOR_FAILURES_MAX ? out.slice(0, PRIOR_FAILURES_MAX) : out;
  }

  /** Resolve one component to {id, priceCents, listCents}, or throw saying why. */
  function resolve(entry, label, mode) {
    if (!entry) {
      throw new Error('no variant data for ' + label);
    }
    if (entry.available === false) {
      throw new Error(label + ' is not available for sale');
    }
    var v = entry[mode];
    if (!v || !v.id) {
      throw new Error('no ' + mode + ' variant for ' + label);
    }
    // CROSS-CHECK THE MIRROR. priceCents is the real variant price the page
    // rendered; unitPriceCents() is what the mode factor says it should be. Two
    // sources for one number -- and until 23 Aug 2026 priceCents was captured here
    // and never read again, which reads like a guard without being one. A
    // maintainer could reasonably have skipped adding a real check because this
    // looked like it already was one. Refusing here catches a mirror that drifted
    // from its list price a step earlier than the submit-time assert does.
    var expectedUnit = pricing.unitPriceCents(entry.listCents, mode);
    if (typeof v.priceCents === 'number' && v.priceCents !== expectedUnit) {
      throw new Error(label + ' is priced ' + v.priceCents + ' but the ' + mode +
                      ' mirror of ' + entry.listCents + ' is ' + expectedUnit);
    }
    return { id: v.id, priceCents: v.priceCents, listCents: entry.listCents };
  }

  /**
   * Build everything a single POST /cart/add.js needs.
   *
   * state  {albums, sets, usb, mode}
   * map    {pricingVersion, albums: {<count>: entry}, photoSet: entry, usb: entry,
   *          guaranteeLine: boolean, guarantee: entry}
   *          entry = {listCents, available, full:{id,priceCents}, deposit:{id,priceCents}}
   *          NOTE listCents sits on the ENTRY, not under full/deposit -- list price
   *          is a property of the album count, and storing it twice per count
   *          would create a divergence the submit-time assert could not catch.
   *          The `guarantee` entry is the SAME shape and goes through the SAME
   *          resolve(): its listCents is 0 and its two mirrors are one id, because
   *          zero is its own 90% and 25% mirror. That 0 is REQUIRED of it and not
   *          merely expected -- the push below refuses any other value, because
   *          resolve()'s mirror check cannot see a listCents whose priceCents is
   *          absent or agrees with it. See the push below.
   *          `guaranteeLine` is the merchant kill switch, rendered from the
   *          `guarantee_line_enabled` THEME setting. ABSENT MEANS ON -- a page
   *          rendered before U10 carries no such key and must keep behaving as it
   *          did, so only an explicit `false` switches the line off.
   * opts   {token, environment, guaranteeNote}
   *
   * Throws rather than returning a partial payload. A refusal the customer can
   * see beats a cart that charges the wrong amount.
   */
  function buildCartPayload(state, map, opts) {
    var o = opts || {};
    var mode = state.mode;
    if (!MODE_LABEL[mode]) throw new Error('unknown payment mode: ' + mode);

    // THE CEILING, AND IT NAMES THE ONE THAT TRIPPED. This quoted the pricing
    // module's ONE shared ceiling constant until it was split in two -- a
    // single number printed for whichever count broke, correct only while
    // albums and sets share a value and silently wrong the moment they do
    // not. The
    // question "which ceiling" is answered ONCE, in rf-pricing.js, because the
    // alternative is re-running the two comparisons here and owning a second
    // copy of a rule the verdict above already applies. Same reason listCents
    // sits on the entry rather than under both mirrors: two sources for one
    // number is a divergence nothing downstream can catch.
    //
    // ⚠ AND IT IS JUDGED AGAINST THE MERCHANT'S LIMITS, NOT THE SHIPPED ONES.
    // `pricing.selfServeLimits(map)` owns the `map &&` guard and the junk
    // policy; this guard runs ABOVE the `map.pricingVersion` read below, so the
    // map really can be null here -- a page whose products did not resolve
    // renders no map at all -- and that page falls back to the shipped numbers
    // rather than throwing. Omitting the argument would leave this refusal
    // judging against 50/100 while the page that drew the submit button judged
    // against the merchant's values, and a customer would meet a refusal for an
    // order the screen had just offered to sell.
    var breach = pricing.selfServeBreach(state, pricing.selfServeLimits(map));
    if (breach) {
      throw new Error(
        'order exceeds the self-serve ceiling of ' + breach.limit + ' ' +
        breach.count + ' — route to a conversation instead'
      );
    }

    // orderAlbums, NEVER clampAlbums. clampAlbums floors at 1 and is shared with
    // the shipped pricing page and the ?albums= handoff, where that floor is
    // correct; here it is the whole defect -- it turns "no albums" into "one
    // album" before any gate downstream can see the zero. Same body, floor 0.
    var albums = pricing.orderAlbums(state.albums);
    var sets = pricing.clampSets(state.sets);
    var usb = !!state.usb;

    // R2: the minimum sellable order is >=1 album OR >=1 photo set. A USB drive
    // is an add-on to a digitization order, not an order -- a cart holding only a
    // USB sells a $24.99 container for images nobody is sending us. Refuse rather
    // than sell, the same call the self-serve ceiling above makes.
    //
    // THIS IS THE WEAKER OF TWO PRECONDITIONS SINCE ALBUM-MIN U2, and it is the
    // only one that holds in both switch positions. The stricter one -- >=1
    // album, while `allow_zero_album_orders` is off -- is the throw below, and
    // this one is FIRST on purpose: an order with nothing in it at all is
    // "nothing to order" whichever way the switch is set.
    //
    // THE PREFIX IS LOAD-BEARING, not decoration. rf-order-cart.js's messageFor()
    // dispatches on the leading token to reach this reason's own merchant-editable
    // copy; without it the customer gets the generic "Something went wrong ...
    // Nothing has been charged" line, which is the wrong sentence for a
    // precondition that stopped the submit before anything was attempted.
    if (albums === 0 && sets === 0) {
      throw new Error(
        'nothing_to_order: an order needs at least one album or one set of loose ' +
        'photos — a USB drive on its own is not a sellable order'
      );
    }

    // THE ALBUM MINIMUM (ALBUM-MIN U2). A SEPARATE REFUSAL FROM THE ONE ABOVE,
    // AND SECOND TO IT ON PURPOSE: an order with nothing in it at all is
    // "nothing to order" whichever way the switch is set, so that reason keeps
    // the emptier state and this one gets the state where the customer HAS asked
    // for something we cannot sell them.
    //
    // `map && map.allowZeroAlbums === true` is the CANONICAL SPELLING of this
    // read, and today this is its only site -- the field and stepper floors add
    // the second. It is pinned by a test rather than by this comment
    // (`ALBUM-MIN U2: the switch read has ONE spelling` in
    // tests/order-builder.test.js), which is more than the `guaranteeLine !==
    // false` pair has: those two are described in both files as deliberately the
    // same expression and nothing asserts it.
    //
    // The `map &&` half is load-bearing rather than defensive.
    // sections/order-builder.liquid renders the variants JSON inside its
    // `sellable` gate while the album field renders outside it, so an unresolved
    // variant produces a page with no map at all, and that page must enforce
    // too. Every absence -- no stored setting, no key, no map -- lands here.
    //
    // READ AFTER THE COUNT, BEFORE THE VARIANT, and both halves matter. `albums`
    // above is deliberately unfloored so this can SEE the zero; flooring it at 1
    // for the enforcing case would be a guard that can never fire. And the album
    // variant is not resolved until the gate below, so switching the minimum on
    // resolves nothing it did not already resolve.
    var allowsZeroAlbums = map && map.allowZeroAlbums === true;
    if (albums === 0 && !allowsZeroAlbums) {
      throw new Error(
        'albums_required: this order has no albums and the album minimum is on — ' +
        'a loose-photos-only order is not sellable while allow_zero_album_orders ' +
        'is off'
      );
    }

    var version = map.pricingVersion || '';
    var items = [];

    // REGRESSION GUARD, the zero-album defect (3 Sep 2026): this push was
    // unconditional, and clampAlbums's floor of 1 made `albums` 1 for a build
    // with no albums in it -- so a customer ordering loose photos alone got one
    // album at $129 in their cart, beside a summary row reading "0 albums — $0".
    // Gated exactly like the photo-set push below.
    if (albums > 0) {
      // R1: the variant is the RATE TIER, the quantity is the real count. Above 20
      // the option value says 20 and the quantity says the truth.
      //
      // RESOLVED INSIDE THE GATE, deliberately. curvePointFor(0) is 1, so
      // resolving above it would look up map.albums[1] on an order with no albums
      // — resolving a variant nobody asked for, and throwing
      // `no variant data for albums (1)` on any map that legitimately lacks a
      // 1-album entry.
      var point = pricing.curvePointFor(albums);
      var albumEntry = resolve(map.albums && map.albums[point], 'albums (' + point + ')', mode);

      items.push({
        id: albumEntry.id,
        quantity: albums,
        properties: {
          // Customer-visible and fulfilment-visible: the count the variant title
          // cannot express above 20. It is ALSO a cart attribute now (below) --
          // see the note there for why both, and which one is authoritative.
          'Albums ordered': String(albums),
          Payment: MODE_TERMS[mode],
          // D15: an immutable snapshot. list_price is a MUTABLE metafield, so
          // recomputing from it after a curve change re-prices a placed order.
          _list_unit_price_cents: centsStr(albumEntry.listCents),
          _pricing_version: version,
        },
      });
    }

    // REGRESSION GUARD, prior bug 2: these two lines were gated on the pay-in-full
    // path, so a deposit customer paid 25% of albums only. On the reference order
    // that under-collected $14.50 and over-stated the balance by the same.
    if (sets > 0) {
      var setEntry = resolve(map.photoSet, 'photo set', mode);
      items.push({
        id: setEntry.id,
        quantity: sets,
        properties: {
          Payment: MODE_TERMS[mode],
          _list_unit_price_cents: centsStr(setEntry.listCents),
          _pricing_version: version,
        },
      });
    }

    if (usb) {
      var usbEntry = resolve(map.usb, 'USB drive', mode);
      items.push({
        id: usbEntry.id,
        quantity: 1,
        properties: {
          Payment: MODE_TERMS[mode],
          _list_unit_price_cents: centsStr(usbEntry.listCents),
          _pricing_version: version,
        },
      });
    }

    // R4: THE GUARANTEE, ON EVERY ORDER THIS BUILDER PLACES -- UNLESS THE MERCHANT
    // HAS SWITCHED THE LINE OFF -- AND ALWAYS LAST.
    //
    // THE SWITCH IS READ BEFORE THE ENTRY IS RESOLVED, AND THAT ORDER IS THE
    // POINT (R7, U10). A missing or misconfigured Guarantee variant REFUSES the
    // order -- Kurtis's decision, taken against dropping the line -- and a refusal
    // that depends on one product picker being correct is a store that a cleared
    // picker takes offline. `guaranteeLine` is the merchant's kill switch, and it
    // only works if nothing about the product is consulted first: switch off means
    // no resolution, no throw, no line, and the store keeps selling. Resolving
    // first and then discarding the result would be the same code in the same
    // order producing the opposite operational behaviour.
    //
    // ABSENT MEANS ON. sections/order-builder.liquid renders the key from the
    // `guarantee_line_enabled` theme setting, which defaults true -- but a page
    // rendered before U10, or a map built by a test written before it, carries no
    // such key, and R4 is the default. Only an explicit `false` switches it off.
    //
    // ONE RESOLVER, ONE ENTRY SHAPE. resolve() does no option matching -- that
    // happens in Liquid, in the map builder, where photoSet and usb already match
    // on option1 and come through this same function. At $0 the variant is
    // exactly its own 90% and 25% mirror: unitPriceCents(0, 'full') and
    // unitPriceCents(0, 'deposit') are both 0, so ONE id sits under both keys at
    // priceCents 0 against listCents 0. Duplicating the id is not a fudge to slip
    // past the mirror cross-check; it is the true statement about a $0 variant,
    // and it leaves that cross-check ARMED. A second resolution path here is what
    // would have disarmed it -- and priceCents has already spent a month in this
    // file looking like a guard without being one.
    //
    // ARMED IS NOT THE SAME AS SUFFICIENT, and that distinction is new on 3 Sep
    // 2026. The mirror cross-check compares two numbers, so it is silent whenever
    // one of them is missing and whenever they agree -- neither of which says the
    // Guarantee is free. The listCents check inside the try below is the part that
    // does, and it is a separate statement rather than a widening of resolve(),
    // which the three MIRRORED components share and whose list prices are
    // legitimately non-zero.
    //
    // LAST, and two things read that: `items[0]` is the album line wherever one
    // exists (pinned in tests/order-builder.test.js), and removeOurLines() unwinds
    // in this order.

    // The id of the Guarantee line this payload carries, or null when the switch
    // is off. Read below by `_lines_digest`, which excludes the Guarantee BY
    // IDENTITY -- so it needs the id, not the entry, and it needs a value that
    // matches nothing at all when there is no Guarantee line to exclude. `null`
    // is safe as that sentinel because every other id here is a variant id.
    var guaranteeLineId = null;

    if (map.guaranteeLine !== false) {
      var guaranteeEntry;
      try {
        guaranteeEntry = resolve(map.guarantee, GUARANTEE_LABEL, mode);

        // THE LIST PRICE, CHECKED SEPARATELY -- resolve()'s mirror cross-check
        // does not cover it, and this is the half that has to be stated on its
        // own. That check is `typeof v.priceCents === 'number' && v.priceCents !==
        // expectedUnit`, so it only fires when BOTH numbers are present AND they
        // disagree. Measured on this tree 3 Sep 2026: a guarantee entry carrying
        // `listCents: 9588` with no `priceCents` at all BUILT a payload, and so
        // did one carrying `listCents: 9588` beside its own consistent full mirror
        // of 8629 -- the second putting a CHARGED line on an order whose Guarantee
        // is meant to be $0.00, past a cross-check that had nothing to disagree
        // with.
        //
        // 0 IS THE CATALOGUE, read exactly as assertCart()'s guarantee tripwire
        // reads it (rf-order-cart.js, the `map.guarantee.listCents !== 0` branch).
        // PK-GUARANTEE-INCLUDED's photokive.list_price is 0 on the store, so any
        // other value is a metafield that was edited, set on the wrong variant, or
        // never set on the right one -- and no checker in this repo can see it:
        // docs/check-catalogue.sh prints that /products/<handle>.js does not
        // expose metafields.
        //
        // `Number(...)` RATHER THAN `!== 0`, so an entry with no listCents at all
        // refuses here (NaN) instead of being read as zero. An entry that cannot
        // state its list price has not stated it is free.
        //
        // IT STAYS ARMED WHATEVER ONE RENDERER EMITS. sections/order-builder.liquid
        // builds this entry's listCents from the metafield; hardcoding a 0 there
        // would stop THAT renderer producing a bad value and would not make this
        // check redundant. The map is merchant-influenced -- the product picker
        // decides which variant the entry describes -- it is assembled elsewhere
        // too (docs/cart-contract.sh extracts one off a rendered page), and nothing
        // between any of them and this line enforces the entry's shape.
        if (Number(guaranteeEntry.listCents) !== 0) {
          throw new Error(
            GUARANTEE_LABEL + ' has a list price of ' + guaranteeEntry.listCents +
            ' and the line is $0.00'
          );
        }
      } catch (e) {
        // A MISSING, UNAVAILABLE OR UNRESOLVABLE GUARANTEE REFUSES THE ORDER. That
        // is the chosen failure mode over dropping the line, and refusing is only
        // defensible if the refusal SAYS WHAT IS WRONG -- otherwise a merchant who
        // clears one product picker takes the whole store offline behind "Something
        // went wrong ... Nothing has been charged".
        //
        // THE PREFIX IS LOAD-BEARING, exactly as `nothing_to_order: ` above is:
        // rf-order-cart.js's messageFor() dispatches on the leading token to reach
        // this reason's own merchant-editable copy (`error_guarantee`).
        //
        // THE INNER TEXT IS KEPT rather than replaced -- resolve()'s, or the list-
        // price check's above. resolve()'s mirror message reads "is priced 9588 but
        // the full mirror of 0 is 0", which is the exact signature of the picker
        // aimed at the annual variant: true, precise, and unreadable as a
        // configuration fault. The clause below is what turns it into an
        // instruction. assertCart() carries the same finding as a tripwire.
        //
        // AND THE CLAUSE NAMES TWO PLACES, NOT ONE (3 Sep 2026). It said only
        // "check the Guarantee product setting in the theme editor", which sends an
        // operator to the picker -- and the picker is not the largest trigger. A
        // non-zero photokive.list_price on the $0.00 variant reaches this same
        // refusal, contributes to no price the customer ever sees, is edited by ops
        // and by apps at a lower trust boundary than the theme editor, and NO
        // CHECKER IN THIS REPO CAN READ IT (docs/check-catalogue.sh says so in its
        // own output: /products/<handle>.js does not expose metafields). An
        // operator who checks only the picker finds it correct and the store stays
        // offline. The message is the whole diagnosis here, because there is no
        // second one to fall back on.
        throw new Error(
          'guarantee_unavailable: ' + (e && e.message ? e.message : String(e)) +
          ' — check the Guarantee product setting in the theme editor, and the ' +
          'photokive.list_price metafield on its $0.00 variant, which no checker here reads'
        );
      }

      // TWO PROPERTIES, AND BOTH OMISSIONS ARE DELIBERATE. Written out here rather
      // than inline because the key is a variable -- this file is ES5 like its two
      // siblings, so there is no computed key in an object literal -- and because
      // the inconsistency wants explaining: the three lines above carry four
      // properties each and this one carries two.
      //
      // NO `Payment`. On the deposit path MODE_TERMS.deposit reads "25% deposit —
      // the balance is invoiced when your images are ready", which is FALSE of a
      // $0.00 line. It is not an internal key either: it prints under the line at
      // checkout, in the confirmation email, and on
      // Order.lineItems.customAttributes, so the falsehood would sit on the order
      // record permanently.
      //
      // NO `_list_unit_price_cents`. Its value here would be the string '0' --
      // byte-identical to what a DROPPED photokive.list_price metafield renders
      // through Liquid's `| default: 0`, which is precisely the failure
      // assertCart()'s photo-set tripwire exists to catch. Writing a legitimate '0'
      // teaches every reader downstream that '0' is a value rather than a symptom.
      // The Guarantee's list price lives in the map entry, and nowhere on the
      // line. TWO checks read it there: the one in the try above, at build time,
      // and assertCart()'s tripwire against the cart that came back.
      //
      // THE NOTE IS FIRST. /cart and Shopify's checkout render properties in
      // insertion order, and the customer-visible one leads.
      //
      // TRIMMED BEFORE THE FALLBACK IS DECIDED, AND `||` ALONE WAS NOT ENOUGH.
      // This read `String(o.guaranteeNote || '') || GUARANTEE_NOTE` until 3 Sep
      // 2026, which covers absent, null and '' and lets every other blank through
      // as content. Measured on this tree: `guaranteeNote: ' '` wrote
      // `Membership: " "` and `'\n\t '` wrote `Membership: "\n\t "` -- on every
      // order line, permanently, on the order record, at Shopify's checkout and in
      // the confirmation email. `guarantee_line_note` is a TEXTAREA, so a merchant
      // clearing it by holding backspace over a wrapped line is one keystroke from
      // that state, and the setting's own `info` promises the opposite: "Blank uses
      // a built-in line with the same meaning; it never goes silent."
      //
      // SAME TEST AS THE SIBLING WRITTEN IN THE SAME PASS. rf-order-builder.js's
      // liveSentence() decides its blank-setting fallbacks with `.trim() !== ''`,
      // for the same reason and against the same class of input; a requirement a
      // text field can switch off is not a requirement. The trim decides WHICH
      // string is used and never edits it -- a merchant's sentence reaches the
      // order record exactly as typed, leading and trailing space included, the
      // same way liveSentence() returns its untrimmed template.
      var note = o.guaranteeNote == null ? '' : String(o.guaranteeNote);
      var guaranteeProps = {};
      guaranteeProps[GUARANTEE_NOTE_KEY] = note.trim() !== '' ? note : GUARANTEE_NOTE;
      guaranteeProps._pricing_version = version;

      // THE SELLING PLAN IS A SIBLING OF id/quantity/properties, NEVER A PROPERTY.
      // Inside `properties` it would become a customer-visible line-item property,
      // join the key set docs/product-spec.md sec 9 enumerates for Kive, and fail
      // the "exactly two properties" assertion in tests/order-builder.test.js. As a
      // sibling it changes no cart attribute and no digest: _lines_digest projects
      // `id + ':' + quantity` and excludes this line by variant id anyway.
      //
      // ADDED ONLY FOR A POSITIVE INTEGER, and absent otherwise -- never `null`.
      // sections/order-builder.liquid omits the map key entirely when its snippet
      // resolved nothing, so `undefined` here is the normal shipped state, not an
      // error. This is the RENDER-TIME half of R4, and as of 11 Sep 2026 it is the
      // ONLY half. ⚠ This said "the submit-time half is the retry in
      // rf-order-cart.js, and it is the half that covers a stale tab". There is no
      // submit-time half; the retry was built three times and removed in 8d0cbde
      // because a browser cannot tell a write that failed from one whose response
      // was lost. NOTHING covers a stale tab -- that order fails atomically, nothing
      // is charged, and a reload fixes it. See the block comment above the add in
      // assets/rf-order-cart.js, and the matching note in
      // snippets/guarantee-selling-plan.liquid.
      //
      // ⚠ AND THAT FAILURE IS NO LONGER A BARE EXIT, as of later the same day. It
      // falls into the submit's own clean-up instead of throwing above it, which
      // zeroes the lines and clears `_pricing_version` -- the key written a few
      // lines up -- in one write. Still no submit-time half, still no retry; what
      // changed is the sentence the CUSTOMER reads when the clean-up cannot prove
      // the cart is empty, which is that the cart may still be holding the order.
      // `_pricing_version` cleared there is also what shuts the /cart re-add's gate
      // on that cart, so the two writers of this key now agree about a failed
      // submit rather than disagreeing about it.
      var guaranteeLine = {
        id: guaranteeEntry.id,
        quantity: 1,
        properties: guaranteeProps,
      };
      // TYPE-GATED BEFORE Number(), because Number(true) is 1 -- a boolean `true`
      // in the map would otherwise have been accepted as selling plan id 1 and sent
      // to the store. Caught by the malformed-value test, not by review.
      // A NUMERIC STRING IS ACCEPTED DELIBERATELY: the /cart side reads this off a
      // data- attribute, which is always a string, so rejecting strings here would
      // make the two writers disagree by construction -- the exact divergence the
      // shared Liquid snippet exists to prevent.
      var rawPlan = map.guaranteeSellingPlan;
      var plan = (typeof rawPlan === 'number' || typeof rawPlan === 'string')
        ? Number(rawPlan) : NaN;
      if (isFinite(plan) && plan > 0 && Math.floor(plan) === plan) {
        guaranteeLine.selling_plan = plan;
      }
      items.push(guaranteeLine);
      guaranteeLineId = guaranteeEntry.id;
    } // the Guarantee line ends here; everything above it is gated

    var listCents = pricing.listSubtotalCents(state);
    var basisCents = pricing.chargedCents(state, 'deposit');
    var isDeposit = mode === 'deposit';

    var attributes = {
      _pricing_version: version,
      _payment_mode: MODE_LABEL[mode],
      _order_value_list_cents: centsStr(listCents),
      // Written on both paths: it is what makes the balance recoverable, and on
      // the pay-in-full path it still records what a deposit would have been.
      _deposit_basis_cents: centsStr(basisCents),
      // Promo-independent, so correct however the customer pays: `basisCents` is
      // chargedCents(state, 'deposit') and no code parameter exists anywhere in
      // that chain, so no promotion can move this value.
      //
      // CORRECTED 2 Sep 2026. This said "present-but-empty, never absent", which
      // is the opposite of what rf-order-cart.js:377-380 says and of what the
      // harness measures. Shopify normalises an attribute written as '' to
      // DELETED, so on the pay-in-full path the cart holds NO SUCH KEY --
      // asserted live by docs/cart-contract.sh, which checks
      // `'_balance_due_later_cents' in a` is False there and passes.
      // "Present but empty" is not a state the cart can hold.
      // AND THE HOP IS MEASURED TOO, 2 Sep 2026: absence SURVIVES the cart ->
      // Order.customAttributes. Order #1005 (pay in full) carries no
      // _balance_due_later_cents key at all -- not an empty one. So a consumer
      // must treat ABSENT as "no balance owed" on the pay-in-full path, and
      // must NOT read absence as a theme bug. Record: docs/promo-code-test-2026-09.md.
      _balance_due_later_cents: isDeposit ? centsStr(listCents - basisCents) : '',
      _builder_token: String(o.token || ''),
      // THE GUARANTEE IS EXCLUDED, BY VARIANT IDENTITY AND NEVER BY PRICE.
      //
      // Two reasons, and the second is the one that decides it. (1) U11 re-adds
      // the line on /cart when a customer has removed it, which mutates the lines
      // after this digest was frozen -- so including it manufactures drift on
      // orders that are entirely legitimate. (2) A $0 line can be
      // quantity-edited to 999 through /cart/change.js for nothing, which would
      // let anyone move the digest and watch whether Kive's check fires, probing
      // an anti-tamper control for free and without putting money through
      // checkout.
      //
      // FILTERED ON THE ID, not on `priceCents === 0` and not on position: a
      // repriced or swapped guarantee variant must not be able to slip the check
      // by still costing nothing, and a future reordering of the pushes above
      // must not silently change what this names. docs/product-spec.md §9's scope
      // statement says the same thing in prose (U14).
      //
      // WITH THE LINE SWITCH OFF `guaranteeLineId` is null and this filter drops
      // nothing, which is the right answer: there is no Guarantee line to exclude
      // and every line in `items` is an order line. Reading the entry here instead
      // would have thrown on the very configuration the switch exists to keep
      // selling.
      _lines_digest: items
        .filter(function (i) { return i.id !== guaranteeLineId; })
        .map(function (i) { return i.id + ':' + i.quantity; })
        .join(','),
      _environment: String(o.environment || ''),
      // D18. Empty on a first-attempt order, which is the common case. Reports
      // only failures that were RECOVERED -- an abandoned failure has no
      // successful submit to ride on, so this is a ratio signal and never a
      // measure of abandonment. Capped, because attribute size limits are
      // undocumented.
      _prior_submit_failures: formatPriorFailures(o.priorFailures),
      // Customer-visible, for fulfilment.
      //
      // WRITTEN ON EVERY PATH — 0, 1 and N — and not only on the zero-album one.
      // Written only at zero it would be ABSENT from every >=1-album order,
      // because the retired-keys loop below writes '' and Shopify normalises that
      // to deleted. The ambiguity this key exists to remove — "no album line: did
      // they order none, or did a line get dropped?" — would then survive on the
      // common path and be removed only on the rare one. Worse, /cart/clear.js
      // does NOT clear attributes, so a '0' left behind by an abandoned sets-only
      // build would ride onto a later 6-album order.
      //
      // DUAL SCOPE, intended. At >=1 album this name is ALSO a line-item property
      // on the album line, where it prints beside the line at checkout and on the
      // packing slip; here it is a cart-level fact that survives having no album
      // line at all. docs/product-spec.md §9 says which one a consumer treats as
      // authoritative (U14) — and BOTH are customer-writable, so neither
      // establishes the album count on its own.
      'Albums ordered': String(albums),
      'Photo sets': String(sets),
      USB: usb ? 'yes' : 'no',
    };

    RETIRED_ATTRIBUTE_KEYS.forEach(function (k) {
      // Only if the current contract does not use the name. Guards against a
      // future rename silently blanking a live key.
      if (!(k in attributes)) attributes[k] = '';
    });

    return {
      items: items,
      attributes: attributes,
    };
  }

  return {
    PRIOR_FAILURES_MAX: PRIOR_FAILURES_MAX,
    formatPriorFailures: formatPriorFailures,
    RETIRED_ATTRIBUTE_KEYS: RETIRED_ATTRIBUTE_KEYS,
    MODE_LABEL: MODE_LABEL,
    MODE_TERMS: MODE_TERMS,
    // Exported so tests drive the REAL strings rather than retyping them: a
    // reworded note or a renamed key then fails the assertion instead of quietly
    // shipping a different sentence to every order record.
    GUARANTEE_NOTE_KEY: GUARANTEE_NOTE_KEY,
    GUARANTEE_NOTE: GUARANTEE_NOTE,
    GUARANTEE_LABEL: GUARANTEE_LABEL,
    buildCartPayload: buildCartPayload,
  };
});
